/**
 * @fileoverview Нормализация ответов от сервисов
 * @description Преобразование различных форматов ответов в единый формат для mention
 */

import {
  RESPONSE_DATA_TYPES,
  DICTIONARY_FIELD_NAMES,
  LOCALES,
} from "@/shared/mention/constants";

/**
 * Проверяет, является ли объект справочником
 * @param {*} value - Значение для проверки
 * @returns {boolean} true, если объект является справочником
 */
export function isDictionary(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  return DICTIONARY_FIELD_NAMES.some((fieldName) => fieldName in value);
}

/**
 * Получает локализованное название из справочника
 * @param {Object} dictionary - Объект справочника
 * @param {string} locale - Локаль (ru, kz, en)
 * @returns {string} Локализованное название
 */
export function getLocalizedName(dictionary, locale = LOCALES.RUSSIAN) {
  if (!dictionary || typeof dictionary !== "object") {
    return "";
  }

  const localeMap = {
    [LOCALES.KAZAKH]: ["name_kk", "name_kz", "name", "name_ru"],
    [LOCALES.RUSSIAN]: ["name_ru", "name", "name_kk", "name_kz"],
    [LOCALES.ENGLISH]: ["name_en", "name", "name_ru"],
  };

  const fallbackOrder = localeMap[locale] || localeMap[LOCALES.RUSSIAN];

  for (const field of fallbackOrder) {
    if (dictionary[field]) {
      return dictionary[field];
    }
  }

  return "";
}

/**
 * Определяет тип данных
 * @param {*} value - Значение для анализа
 * @returns {string} Тип данных
 */
export function detectDataType(value) {
  if (value === null || value === undefined) {
    return RESPONSE_DATA_TYPES.PRIMITIVE;
  }

  if (Array.isArray(value)) {
    return RESPONSE_DATA_TYPES.ARRAY;
  }

  if (typeof value === "object") {
    if (isDictionary(value)) {
      return RESPONSE_DATA_TYPES.DICTIONARY;
    }
    return RESPONSE_DATA_TYPES.OBJECT;
  }

  return RESPONSE_DATA_TYPES.PRIMITIVE;
}

/**
 * Нормализует массив примитивов или справочников
 * @param {Array} array - Массив для нормализации
 * @param {string} locale - Локаль
 * @returns {string} Строка со значениями через запятую
 */
export function normalizeSimpleArray(array, locale) {
  if (!Array.isArray(array) || array.length === 0) {
    return "";
  }

  const values = array
    .map((item) => {
      if (item && typeof item === "object") {
        return getLocalizedName(item, locale);
      }
      return item ?? "";
    })
    .filter(Boolean);

  return values.join(", ");
}

/**
 * Проверяет, является ли массив простым (только примитивы и справочники)
 * @param {Array} array - Массив для проверки
 * @returns {boolean} true, если массив простой
 */
export function isSimpleArray(array) {
  if (!Array.isArray(array)) {
    return false;
  }

  return array.every(
    (item) => !item || typeof item !== "object" || isDictionary(item)
  );
}

/**
 * Нормализует значение в строку для отображения
 * @param {*} value - Значение
 * @param {string} locale - Локаль
 * @returns {string} Нормализованное значение
 */
export function normalizeValue(value, locale = LOCALES.RUSSIAN) {
  if (value === null || value === undefined) {
    return "";
  }

  const dataType = detectDataType(value);

  switch (dataType) {
    case RESPONSE_DATA_TYPES.DICTIONARY:
      return getLocalizedName(value, locale);

    case RESPONSE_DATA_TYPES.ARRAY:
      if (isSimpleArray(value)) {
        return normalizeSimpleArray(value, locale);
      }
      return ""; // Сложные массивы обрабатываются отдельно

    case RESPONSE_DATA_TYPES.PRIMITIVE:
      return String(value);

    case RESPONSE_DATA_TYPES.OBJECT:
      return ""; // Объекты обрабатываются отдельно

    default:
      return String(value);
  }
}

/**
 * Экспорт всех функций для использования в других модулях
 */
export default {
  isDictionary,
  getLocalizedName,
  detectDataType,
  normalizeSimpleArray,
  isSimpleArray,
  normalizeValue,
};
